import { setEmbedAuth } from "./auth";

// Viewer <-> embedding-host postMessage handshake.
//
//  - The viewer never receives its credential in the URL. On load it announces
//    `ready` to its framing host (bounded retry) and the host replies with the
//    API key via postMessage.
//  - `ready` is posted with an exact `targetOrigin` — never `"*"` — and only
//    to a validated, allowlisted host origin (fail-closed when unknown).
//  - The inbound `auth` reply must come from an allowlisted origin AND from
//    `window.parent` (`event.source` pinning), so a forged-origin or
//    same-origin-different-frame message can't inject a key.

export const EMBED_MESSAGE_SOURCE = "aiera-embed";

interface ReadyMessage {
  source: typeof EMBED_MESSAGE_SOURCE;
  type: "ready";
}

interface AuthMessage {
  source: typeof EMBED_MESSAGE_SOURCE;
  type: "auth";
  apiKey: string;
}

// Origins that may embed the viewer and deliver a key, from per-environment
// config (env.ts validates the format). The same value is pasted into the CSP
// frame-ancestors list at deploy time, so the two layers can't drift: the CSP
// blocks the framing; this gates the handshake.
const CONFIGURED_PARENT_ORIGINS = (
  (import.meta.env.VITE_ALLOWED_PARENT_ORIGINS as string | undefined) ?? ""
)
  .trim()
  .split(/\s+/)
  .filter(Boolean);

// Local dev parents (the in-repo dev-host, the web-components demo host — see
// https://github.com/aiera-inc/web-components — or any localhost host page).
// Compiled out of production builds via the import.meta.env.DEV default.
const DEV_HOST_HOSTNAMES = ["localhost", "127.0.0.1"];

export interface HandshakeOptions {
  /** Test/dev seams — default to production behavior and the env config. */
  allowLocalhost?: boolean;
  allowedParentOrigins?: string[];
  hostOrigin?: string;
  parentWindow?: Window;
}

/**
 * Match one allowlist entry: `https://*.host` wildcards match subdomains by
 * suffix (not the apex, and only on the default port — mirroring how CSP
 * matches the same entry in frame-ancestors; an apex is allowed via its own
 * exact entry), while every other entry must equal the browser-asserted
 * origin exactly.
 */
function matchesEntry(entry: string, url: URL): boolean {
  if (entry.startsWith("https://*.")) {
    const suffix = entry.slice("https://*".length); // ".vendor.com"
    return url.hostname.endsWith(suffix) && url.port === "";
  }
  return url.origin === entry;
}

/**
 * Validate a framing-host origin against the configured allowlist. This is a
 * structural check on the parsed URL — not a substring test — so lookalikes
 * such as `https://aiera.com.evil.example` or `https://evil-aiera.com` are
 * rejected.
 */
export function isAllowedHostOrigin(
  origin: string,
  {
    allowLocalhost = import.meta.env.DEV,
    allowedParentOrigins = CONFIGURED_PARENT_ORIGINS,
  }: HandshakeOptions = {},
): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  if (allowLocalhost && DEV_HOST_HOSTNAMES.includes(url.hostname)) {
    return url.protocol === "http:" || url.protocol === "https:";
  }
  if (url.protocol !== "https:") return false;
  return allowedParentOrigins.some((entry) => matchesEntry(entry, url));
}

function isAuthMessage(data: unknown): data is AuthMessage {
  return (
    !!data &&
    typeof data === "object" &&
    (data as AuthMessage).source === EMBED_MESSAGE_SOURCE &&
    (data as AuthMessage).type === "auth" &&
    typeof (data as AuthMessage).apiKey === "string" &&
    (data as AuthMessage).apiKey.length > 0
  );
}

// The shell gives up waiting for `auth` after this long and shows the
// handshake-timeout error state.
export const HANDSHAKE_TIMEOUT_MS = 6_000;

// Bounded retry so a `ready` dropped by the host (its listener not yet
// attached, or its API key not yet set) is re-sent rather than lost. Stops as
// soon as the host replies with `auth`. The retries span the full handshake
// window: a stateless host that only answers `ready` when it holds a key gets
// a fresh chance every interval until the shell gives up — stopping earlier
// would leave key-arrives-late handshakes permanently dead.
const READY_RETRY_INTERVAL_MS = 300;
const READY_MAX_ATTEMPTS = HANDSHAKE_TIMEOUT_MS / READY_RETRY_INTERVAL_MS;

/**
 * Resolve the framing host's origin for the outbound `ready`. Prefers
 * `ancestorOrigins` (the exact immediate-parent origin, on Chrome/Safari);
 * falls back to `document.referrer` (the Firefox path). Returns "" when
 * neither is available — the handshake then fails closed (no `ready` is ever
 * posted to an unknown origin). Note this value is only ever used as a
 * `postMessage` target; the origin recorded for analytics comes from the
 * validated inbound `auth` event, never from the referrer.
 */
function resolveHostOrigin(): string {
  const ancestors = window.location.ancestorOrigins;
  if (ancestors && ancestors.length > 0) return ancestors[0];
  try {
    return document.referrer ? new URL(document.referrer).origin : "";
  } catch {
    return "";
  }
}

/**
 * Start the viewer side of the handshake. Returns a cleanup function. When the
 * host replies with a valid `auth`, the key and the event's browser-asserted
 * origin land in the auth store (src/services/auth.ts). Later valid `auth`
 * messages keep updating the store, so a host may rotate the key.
 */
export function startEmbedHandshake(
  options: HandshakeOptions = {},
): () => void {
  const hostOrigin = options.hostOrigin ?? resolveHostOrigin();
  const parentWindow = options.parentWindow ?? window.parent;
  let timer: ReturnType<typeof setInterval> | undefined;

  const stopRetry = () => {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };

  const onMessage = (event: MessageEvent) => {
    if (!isAllowedHostOrigin(event.origin, options)) return;
    if (event.source !== parentWindow) return;
    if (!isAuthMessage(event.data)) return;

    stopRetry();
    setEmbedAuth({ apiKey: event.data.apiKey, parentOrigin: event.origin });
  };

  window.addEventListener("message", onMessage);

  const startReadyRetry = (targetOrigin: string) => {
    const ready: ReadyMessage = { source: EMBED_MESSAGE_SOURCE, type: "ready" };
    const postReady = () => parentWindow.postMessage(ready, targetOrigin);
    postReady();

    let attempts = 0;
    timer = setInterval(() => {
      attempts += 1;
      if (attempts >= READY_MAX_ATTEMPTS) {
        stopRetry();
        return;
      }
      postReady();
    }, READY_RETRY_INTERVAL_MS);
  };

  if (hostOrigin && isAllowedHostOrigin(hostOrigin, options)) {
    startReadyRetry(hostOrigin);
  } else if (hostOrigin) {
    // A framing host we refuse to talk to. An empty hostOrigin is not an
    // error: it means top-level (no framing host at all) or an unresolvable
    // parent origin — the handshake fails closed (no `ready` is posted) and
    // the shell times out into its handshake state. Note the unresolvable
    // case includes Firefox (no `ancestorOrigins`) with a suppressed referrer.
    console.error("Embed handshake: framing host origin not allowlisted", {
      hostOrigin,
    });
  }

  return () => {
    window.removeEventListener("message", onMessage);
    stopRetry();
  };
}
